import { message } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { createBrowserHistory } from 'history'
import { User } from '../../reducers/userReducer'
import { Organization } from '../../reducers/organizationReducer'
import { setIsOnboarded, PlayerState } from '../../reducers/playersReducer'
import { AppContent } from '../../components/utils/utils'
import { useAxiosWithHeader, useQuery } from '../../utils'
import { useEffectOnce } from 'react-use'
import { Redirect } from 'react-router'
import getOrgAndEngineers from '../../api/getOrgAndEngineers'
import LoadingPage from '../Loading'
import { URLBACK } from '../../assets/urls'
import { Engineer } from '../../reducers/engineerReducer'
import { fetchPullRequestsAndUpdateReducer } from '../OpenChannels/fetchPullRequest'
import ModalErrorInstallation from '../../components/ModalErrorInstallation'
import { isSlackAuth } from '../../components/TeamChannels/helpers'
import { handleLogout } from '../../components/logoutButton/handleLogout'
import { fetchSlackChannelsAndUpdateReducer } from '../../components/Settings/utils'

async function refreshUser({ axios, dispatch, user }) {
  if (!user?.id) return user

  try {
    const resp = await axios.get('/users/' + user.id)
    if (resp?.data?.email?.length > 0) {
      const updatedUser = {
        ...user,
        ...resp.data,
        slackAuth: resp.data.slackAuth ?? user.slackAuth,
        providerAuth: resp.data.providerAuth ?? user.providerAuth,
      }
      dispatch(User.actions.updateUser(updatedUser))
      return updatedUser
    }
  } catch (error) {
    console.log('updateUser error', error)
  }

  return user
}

export function Home() {
  const history = createBrowserHistory()
  let prevPath = null

  history.listen((location) => {
    if (location.pathname !== prevPath) {
      prevPath = location.pathname
      window.analytics.page()
    }
  })
  const [redirect, setRedirect] = useState(false)
  const user = useSelector(User.selectors.selectUser, isEqual)
  const organization = useSelector(Organization.selectors.getOrganization, isEqual)
  const isOnboarded = useSelector(PlayerState.selectors.isOnboarded)
  const isMountedRef = useRef(true)
  const slackCallbackHandledRef = useRef(false)
  const githubSetupHandledRef = useRef(false)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // This is so that getOrgAndEngineers only gets called once at a time.
  let query = useQuery()
  const axios = useAxiosWithHeader()

  const dispatch = useDispatch()

  const slackAuth = isSlackAuth({ user, organization })

  const githubAuth = user.organizations?.[0]?.providerLogin?.length > 0

  // Refresh persisted user data before loading an organization. OAuth callbacks
  // update the user in their own effects so their writes cannot race this GET.
  useEffectOnce(() => {
    let active = true

    ;(async () => {
      const githubSetup = query.get('github')
      const slackSetup = query.get('slack')
      const githubAppSetup = query.get('setup_action')
      const successfulSlackSetup = slackSetup && slackSetup !== 'error'

      if (slackSetup === 'error') {
        message.destroy()
        message.error(
          'It seems there was an error when installing Slack. Please try again or contact support',
        )
      }

      if (successfulSlackSetup || githubAppSetup) return

      const shouldLoadGithubSetupOrg = githubSetup && organization.players?.length === 0
      const shouldLoadSlackMembers = !organization.slackMembers

      const userForOrgLoad = githubSetup
        ? user
        : await refreshUser({ axios, dispatch, user })
      if (!active || !userForOrgLoad) return

      const shouldLoadOrganization =
        !organization.id || shouldLoadGithubSetupOrg || shouldLoadSlackMembers
      if (!shouldLoadOrganization || (githubSetup && userForOrgLoad.requestGithubApp))
        return

      if (githubSetup) {
        // The GitHub callback can finish attaching the organization just after redirect.
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      if (!active) return

      try {
        await getOrgAndEngineers({
          organization,
          dispatch,
          user: userForOrgLoad,
          axios,
          setIsOnboarded,
        })
      } catch (error) {
        console.log('error loading organization', error)
      }
    })()

    return () => {
      active = false
    }
  })

  // I don't understand this useEffect, some comments would be nice! :)
  useEffect(() => {
    ;(async () => {
      const slackSetup = query.get('slack')
      const isSuccessfulSetup = slackSetup && slackSetup !== 'error'
      if (user && isSuccessfulSetup && !slackCallbackHandledRef.current) {
        slackCallbackHandledRef.current = true
        try {
          let updatedUser = user
          if (!user.slackAuth) {
            const { data } = await axios.put('/users/' + user.id, {
              slackAuth: true,
            })
            updatedUser = { ...user, ...data, slackAuth: true }
            dispatch(User.actions.updateUser(updatedUser))
          } else {
            updatedUser = await refreshUser({ axios, dispatch, user })
          }
          message.destroy()
          message.success('Slack successfully installed', 2.5)

          // TODO should call those two functions in other places
          // May be when someone loggs in, so it gets refreshed in case there are new members
          await getOrgAndEngineers({
            organization,
            dispatch,
            user: updatedUser,
            axios,
            setIsOnboarded,
          })
        } catch (e) {
          console.error({ e })
          message.error('Error, please try again or contact support')
        }
      }
    })()
  }, [axios, dispatch, query, user, organization])

  // act that user has connected his github
  useEffect(() => {
    ;(async () => {
      const isSetup = query.get('setup_action')
      if (user && isSetup && !githubSetupHandledRef.current) {
        githubSetupHandledRef.current = true
        try {
          let updatedUser = user
          if (!user.providerAuth) {
            const { data } = await axios.put('/users/' + user.id, {
              providerAuth: true,
            })
            updatedUser = { ...user, ...data, providerAuth: true }
            dispatch(User.actions.updateUser(updatedUser))
          } else {
            updatedUser = await refreshUser({ axios, dispatch, user })
          }

          if (!organization.id && updatedUser?.organizations?.length > 0) {
            await getOrgAndEngineers({
              invocation: 10,
              organization,
              dispatch,
              user: updatedUser,
              axios,
              setIsOnboarded,
            })
          }

          message.destroy()
          const hasProviderOrganization =
            updatedUser.organizations?.[0]?.providerLogin?.length > 0
          if (
            updatedUser.providerAuth &&
            !hasProviderOrganization &&
            updatedUser.target_type === 'user'
          ) {
            message.error(
              'Axolo can only be installed on Organizations. Please install Axolo on an organization.',
            )
          } else if (updatedUser.providerAuth && !hasProviderOrganization) {
            message.error(
              'It seems you do not have admin right on Github or that you tried to install Axolo on a personnal account, please install Axolo on your organization & come back!',
              10,
            )
          } else if (updatedUser.providerAuth && hasProviderOrganization) {
            message.success('GitHub app successfully installed')
          }
        } catch (e) {
          console.error({ e })
          message.error('Error, please try again or contact support')
        }
      }
    })()
  }, [axios, dispatch, query, user, organization])

  // if admin has providerAuth === true but no organization that means it did not have the right to install the app in its organization
  useEffect(() => {
    ;(async () => {
      const activePlayers =
        organization.players?.filter((p) => p.communicationToolId?.length > 0) || []

      // If User install from GitHub and we are waiting for him to add Slack it goes here.
      if (
        slackAuth === false &&
        organization?.sender?.length > 0 &&
        user.username === organization.sender &&
        activePlayers.length === 0 &&
        isOnboarded === null
      ) {
        dispatch(setIsOnboarded(false))
      } else if (activePlayers.length > 0 && !isOnboarded) {
        dispatch(setIsOnboarded(true))
      }
      // If user is GitHub Admin who just accepted to install GitHub then we redirect him
      if (
        organization?.requester?.length > 0 &&
        user.provider === 'github' &&
        user?.providerLogin !== organization?.requester
      ) {
        setRedirect(true)
        return
      }
    })()
  }, [dispatch, user, organization, isOnboarded, slackAuth])

  // Load the engineer only after the organization has resolved. This also keeps
  // the avatar in sync when an initially empty persisted organization is refreshed.
  useEffect(() => {
    if (!organization.id) return undefined

    let active = true
    axios
      .get(`${URLBACK}engineer/me/${organization.id}`)
      .then((engineerResp) => {
        if (active) dispatch(Engineer.actions.setData(engineerResp.data))
      })
      .catch((error) => {
        if (active) console.log('error getting engineer info', error)
      })

    return () => {
      active = false
    }
    // useAxiosWithHeader creates a new instance on every render; the org id is
    // the meaningful trigger and avoids repeatedly fetching the same engineer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id, dispatch])

  const [, setLoading] = useState(false)
  // useEffectOnce to check if we have the Slack channels of the organization
  // this should only be useful during the first onboarding or first time and
  // old organization connects after this commit
  useEffect(() => {
    let active = true
    let attempts = 0
    const maxAttempts = 200

    // This is for the onboarding, we want to make sure we have PR
    const fetchPullRequestsRepeatedly = async () => {
      while (active && attempts < maxAttempts && organization.id) {
        attempts += 1
        const pullRequests = await fetchPullRequestsAndUpdateReducer({
          organization,
          user,
          axios,
          dispatch,
          Organization,
          isMountedRef,
          source: 'Home',
        })

        if (pullRequests.length > 0) {
          break
        }

        await new Promise((r) => setTimeout(r, 1000)) // Wait for 1 second before next attempt
      }
    }

    if (organization.id) {
      fetchSlackChannelsAndUpdateReducer({
        organization,
        user,
        axios,
        dispatch,
        setLoading,
      })

      fetchPullRequestsRepeatedly()
    }

    return () => {
      active = false
    }
    // if we add axios here, it will call twice the PR for this org
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id, dispatch, user, setLoading, organization])

  if (user?.providerName === 'autoAssignReviewer') {
    return <Redirect to={'/settings/auto-assign-reviewer'} />
  }

  if (redirect) {
    return <Redirect to={`/githubInstalled/${user.username}`} />
  }
  if (isOnboarded === false) {
    // We track reditus here
    try {
      window.gr('track', 'conversion', { email: user.email })
    } catch (error) {
      console.log('error tracking gr', error)
    }
    // Order matters
    if (!slackAuth && githubAuth) {
      // This means the admin accepted to install Axolo on GitHub
      if (
        organization?.requester?.length > 0 &&
        user.provider === 'github' &&
        user?.providerLogin !== organization?.requester
      ) {
        return <Redirect to={`/githubInstalled/${user.username}`} />
      }
      return <Redirect to={'/home/onboarding/slack'} />
    }
    if (slackAuth && githubAuth) {
      return <Redirect to={'/home/onboarding/members'} />
    }
    if (slackAuth && user.requestGithubApp) {
      return <Redirect to={'/home/onboarding/github'} />
    }
    if (slackAuth && !githubAuth) {
      return <Redirect to={'/home/onboarding'} />
    }
    return (
      <div>
        <AppContent className="mt-4 flex">
          <h1 className="pt-8 text-center text-xl">Something wrong happened</h1>
          <p className="text-center">
            If you are seeing this screen, try to log out and log in again or contact
            support.
          </p>
          <span
            className="flex justify-center pt-8"
            onClick={() => handleLogout(user.jwt, dispatch, axios)}
          >
            <button
              type="primary"
              className="rounded-lg border-none bg-primary px-4 py-1 text-textWhite hover:bg-indigo-700 hover:text-textWhite"
            >
              Logout
            </button>
          </span>
        </AppContent>
      </div>
    )
  }

  // This helps the users redirect to onboarding his members whenever
  // he logs in or refresh data during onboarding
  // when he has never onboarded anyone but has a Git organization
  const activePlayers =
    organization.players?.filter((p) => p.communicationToolId?.length > 0) || []

  if (
    isOnboarded === null &&
    organization?.players?.length > 0 &&
    activePlayers.length === 0 &&
    slackAuth
  ) {
    return <Redirect to={`/home/onboarding/members`} />
  }

  if (isOnboarded === null || !(organization?.players?.length > 0)) {
    return (
      <>
        {organization?.errorInstallation?.length > 0 ? (
          <ModalErrorInstallation
            provider={organization.provider === 'github' ? 'GitHub' : 'GitLab'}
            dispatch={dispatch}
          />
        ) : null}
        <LoadingPage
          footer={false}
          title={'Fetching your information. Can take up to 2 minutes.. '}
        />
      </>
    )
  } else {
    return <Redirect to={`/settings/pull-request-channel`} />
  }
}

export default Home
